import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Typography } from './typography';

export interface TabOption {
  id: string;
  label: string;
  hidden?: boolean;
}

interface TabsProps extends ComponentPropsWithoutRef<'div'> {
  tabs: TabOption[];
  activeTabId: string;
  onChangeTab: (tabId: string) => void;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ tabs, activeTabId, onChangeTab, className, ...props }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollState, setScrollState] = useState({
      left: false,
      right: false,
    });
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

    const update = () => {
      const el = scrollRef.current;
      if (!el) return;
      setScrollState({
        left: el.scrollLeft > 0,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      });
    };

    const visibleTabs = useMemo(
      () => tabs.filter(({ hidden }) => !hidden),
      [tabs]
    );

    const hideDividers = (id: string, visibleIndex: number) => {
      const isFirstTab = visibleIndex === 0;
      const isActiveTab = id === activeTabId;
      const isPrevTabActive = visibleTabs[visibleIndex - 1]?.id === activeTabId;

      const toHide = isFirstTab || isActiveTab || isPrevTabActive;

      return toHide && 'invisible';
    };

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      update();
      el.addEventListener('scroll', update);
      window.addEventListener('resize', update);
      return () => {
        el.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    }, []);

    /**
     * The indicator should glide when you pick a different tab, but not when
     * the tab list itself changes underneath a tab that stayed selected.
     *
     * Switching Portfolio <-> Single Mode keeps you on e.g. Floor Plan while
     * the list grows from 2 tabs to 4, so Floor Plan shifts position and the
     * indicator slid across the bar for no reason. Here we reposition it with
     * the transition switched off, then restore it once the browser has
     * painted.
     */
    const prevActiveTabId = useRef(activeTabId);
    const [animateIndicator, setAnimateIndicator] = useState(true);

    useEffect(() => {
      const activeTab = tabRefs.current.get(activeTabId);
      const container = scrollRef.current;
      if (!activeTab || !container) return;

      const tabChanged = prevActiveTabId.current !== activeTabId;
      prevActiveTabId.current = activeTabId;

      const updateIndicator = () => {
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        setIndicatorStyle({
          left: tabRect.left - containerRect.left + container.scrollLeft,
          width: tabRect.width,
        });
      };

      let restore: number | undefined;
      if (!tabChanged) {
        setAnimateIndicator(false);
        // Two frames: one for the style to land un-animated, one to re-arm.
        restore = requestAnimationFrame(() =>
          requestAnimationFrame(() => setAnimateIndicator(true))
        );
      }

      updateIndicator();

      const resizeObserver = new ResizeObserver(updateIndicator);
      resizeObserver.observe(activeTab);
      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
        if (restore !== undefined) cancelAnimationFrame(restore);
      };
    }, [activeTabId, visibleTabs]);

    const scroll = (dir: 'left' | 'right') =>
      scrollRef.current?.scrollBy({
        left: dir === 'left' ? -200 : 200,
        behavior: 'smooth',
      });

    const fadeBase =
      'pointer-events-none absolute inset-y-0 w-40 from-neutral-2 to-transparent z-10';

    return (
      <div className="relative flex items-center">
        {scrollState.left && (
          <>
            <div className={cn(fadeBase, 'left-0 rounded-l bg-linear-to-r')} />
            <div className="absolute left-2 z-20">
              <Button
                iconed
                variant="secondary"
                aria-label="Scroll left"
                leftIconName="chevron-left"
                onClick={() => scroll('left')}
              />
            </div>
          </>
        )}

        <div
          role="tablist"
          ref={scrollRef}
          className={cn(
            'bg-tab-inactive scrollbar-none relative flex gap-x-1 overflow-x-auto rounded whitespace-nowrap',
            className
          )}
          {...props}
        >
          <div
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
            }}
            className={cn(
              'bg-primary-6 absolute top-0 bottom-0 rounded',
              animateIndicator && 'transition-all duration-300 ease-in-out'
            )}
          />
          {visibleTabs.map(({ id, label }, index) => (
            <button
              role="tab"
              type="button"
              key={id}
              aria-selected={id === activeTabId}
              ref={(el) => {
                if (el) tabRefs.current.set(id, el);
                else tabRefs.current.delete(id);
              }}
              className={cn(
                'relative z-10 flex h-12 items-center rounded px-4 transition-colors duration-200',
                id === activeTabId
                  ? 'text-primary-inverse'
                  : 'text-primary-black'
              )}
              onClick={() => onChangeTab(id)}
            >
              <Typography variant="h5">{label}</Typography>

              <div
                className={cn(
                  'border-neutral-6 absolute h-[75%] -translate-x-[18px] border-r',
                  hideDividers(id, index)
                )}
              />
            </button>
          ))}
        </div>

        {scrollState.right && (
          <>
            <div className={cn(fadeBase, 'right-0 rounded-r bg-linear-to-l')} />
            <div className="absolute right-2 z-20">
              <Button
                iconed
                variant="secondary"
                aria-label="Scroll right"
                leftIconName="chevron-right"
                onClick={() => scroll('right')}
              />
            </div>
          </>
        )}
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';
